import type { Editor } from "@tiptap/core";
import type { Asset } from "schemas/generated/asset";
import type { Product } from "schemas/generated/product";

import React from "react";

import { DebouncedInput } from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useProductList } from "@editor/hooks/useProductList";
import { assetUtils } from "@editor/trpc/asset";

import { IMAGE_ACCEPT_FILE_EXTENSIONS } from "@/features/assets/constants";
import { useReploFileDropZone } from "@/features/assets/useFileDropZone";
import {
  selectAsset,
  selectProduct,
} from "@/features/react-editor/components/chat/utils/contextItem";
import IconButton from "@replo/design-system/components/button/IconButton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@replo/design-system/components/shadcn/core/tabs";
import twMerge from "@replo/design-system/utils/twMerge";
import { ImageIcon, Search, ShoppingBag, Upload, X } from "lucide-react";

import { AssetContextItemsList } from "./AssetContextItemsList";
import { ProductContextItemsList } from "./ProductContextItemsList";

const INITIAL_ITEMS_TO_LOAD = 10;

interface ContextItemsMenuProps {
  onClose?: () => void;
  editor: Editor | null;
}

type ContextItemsMenuTab = "assets" | "products";

export const ContextItemsMenu = ({
  onClose,
  editor,
}: ContextItemsMenuProps) => {
  const projectId = useCurrentProjectId();
  const [currentTab, setCurrentTab] =
    React.useState<ContextItemsMenuTab>("products");
  const [searchQuery, setSearchQuery] = React.useState("");
  const menuRef = React.useRef<HTMLDivElement>(null);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    droppedFilesMap,
    openFileDialog,
  } = useReploFileDropZone({
    onUploadComplete: () => {
      void assetUtils.list.invalidate();
    },
    acceptDropAssetType: IMAGE_ACCEPT_FILE_EXTENSIONS,
    allowClickToUpload: true,
  });

  const {
    products,
    isLoading: isLoadingProducts,
    fetchNextPage: fetchNextProductsPage,
    hasNextPage: hasNextProductsPage,
  } = useProductList({
    projectId,
    limit: INITIAL_ITEMS_TO_LOAD,
    search: searchQuery,
  });

  const tabs = [
    {
      value: "products",
      label: "Products",
      icon: <ShoppingBag className="size-3" />,
    },
    {
      value: "assets",
      label: "Assets",
      icon: <ImageIcon className="size-3" />,
    },
  ];

  const handleProductSelect = (product: Product) => {
    selectProduct(product, editor);
    onClose?.();
  };

  const handleAssetSelect = (asset: Asset) => {
    selectAsset(asset, editor);
    onClose?.();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-row justify-between items-center p-3 border-b-0.5 border-border">
        <span className="font-medium text-default text-base">
          Add a reference
        </span>
        <IconButton
          variant="tertiary"
          size="sm"
          onClick={onClose}
          icon={<X className="h-4 w-4" />}
        />
      </div>

      <Tabs
        value={currentTab}
        onValueChange={(value) => setCurrentTab(value as ContextItemsMenuTab)}
        className="flex flex-row h-full overflow-hidden max-h-[400px]"
      >
        <div className="p-3 border-r-0.5 border-border">
          <TabsList className="flex flex-col gap-1 p-0 h-full justify-start bg-transparent">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className={twMerge(
                  "flex flex-row justify-start w-full gap-2 items-center p-1.5 rounded-md transition-colors cursor-pointer hover:bg-menu-item-selected/50 text-sm focus:outline-none focus-visible:ring-primary focus-visible:ring-offset-0 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-offset-transparent shadow-none",
                  "data-[state=active]:bg-menu-item-selected data-[state=active]:shadow-none",
                )}
              >
                {tab.icon}
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <div className="w-full px-1 py-3 flex-1 pb-8 overflow-x-auto overflow-y-hidden">
          <div className="flex flex-row items-center gap-2 px-2">
            <DebouncedInput
              placeholder="Search"
              startEnhancer={<Search size={14} />}
              size="sm"
              value={searchQuery}
              onValueChange={setSearchQuery}
              layoutClassName="w-full"
              autoFocus={true}
              onKeyDown={(e) => {
                // Handle tab navigation with arrow keys
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  // Focus the first item in the current list
                  const listContainer = menuRef.current?.querySelector(
                    `[data-list-container="${currentTab}"]`,
                  ) as HTMLElement;
                  if (listContainer) {
                    listContainer.focus();
                    // Trigger focus first item by dispatching ArrowDown
                    listContainer.dispatchEvent(
                      new KeyboardEvent("keydown", {
                        key: "ArrowDown",
                        bubbles: true,
                        cancelable: true,
                      }),
                    );
                  }
                  return;
                }

                if (e.key === "Escape") {
                  onClose?.();
                }
              }}
            />
            {currentTab === "assets" && (
              <IconButton
                icon={<Upload size={12} />}
                variant="secondary"
                onClick={openFileDialog}
                size="sm"
              />
            )}
          </div>
          <TabsContent
            value="products"
            className="h-full flex-1 overflow-y-auto"
            id="context-menu-scroll-container"
          >
            <ProductContextItemsList
              products={products}
              isLoading={isLoadingProducts}
              searchQuery={searchQuery}
              fetchNextProductsPage={() => void fetchNextProductsPage()}
              hasNextProductsPage={hasNextProductsPage}
              onProductSelect={handleProductSelect}
            />
          </TabsContent>
          <TabsContent value="assets" className="h-full">
            <AssetContextItemsList
              onAssetSelect={handleAssetSelect}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              droppedFilesMap={droppedFilesMap}
              isDragActive={isDragActive}
              searchQuery={searchQuery}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};
